import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './UseCaseSection.css';

const useCases = [
  {
    image: './img/caro.webp',
    name: 'Carolin Kindermann',
    role: { en: 'Business Coach', de: 'Business Coach' },
    company: '',
    website: 'https://carolinkindermann.de',
    industry: { en: 'Coaching', de: 'Coaching' },
    before: 4,
    after: 24,
    timeframe: { en: '90 days', de: '90 Tage' },
    quote: {
      en: 'BonusClub helps me consistently win clients, partners, and new contacts for my network. I used to rely heavily on referrals and social media. Good phases were great, but they were never controllable.',
      de: 'BonusClub hilft mir dabei, planbar Kunden, Partner und neue Kontakte für mein Netzwerk zu gewinnen. Früher war ich stark von Empfehlungen und Social Media abhängig - gute Phasen waren top, aber sie waren nie steuerbar.',
    },
  },
  {
    image: './img/wavy_2_1.jpg',
    name: 'Wavy Magazine',
    role: { en: 'International Music Magazine', de: 'International Music Magazine' },
    company: 'Wavy Magazine',
    website: 'https://wavymagazine.com',
    industry: { en: 'Music Magazine', de: 'Music Magazine' },
    before: 0,
    after: '50+',
    unit: { en: 'sales / mo', de: 'Sales / Mo' },
    timeframe: { en: '120 days', de: '120 Tage' },
    quote: {
      en: 'Valerie and the team really took the time to understand our magazine and how we operate. Together, we built a setup that keeps bringing in steady revenue, and it has been running for over two years now.',
      de: 'Valerie und das Team haben sich wirklich Zeit genommen, unser Magazin und unsere Arbeitsweise zu verstehen. Gemeinsam haben wir ein Setup aufgebaut, das kontinuierlich Umsatz bringt - und das läuft jetzt seit über zwei Jahren.',
    },
  },
  {
    image: './img/lammle.webp',
    name: 'Sebastian Lämmle',
    role: { en: 'Fitness & Coaching', de: 'Fitness & Coaching' },
    company: '',
    website: 'https://www.sebastian-laemmle.com/',
    industry: { en: 'Fitness & Coaching', de: 'Fitness & Coaching' },
    before: 2,
    after: 36,
    timeframe: { en: '60 days', de: '60 Tage' },
    quote: {
      en: 'I was great working with the BonusClub team to increase my new coaching brand visibility and generate calls with potential customers and partners. This way I could learn a lot about my target audience and improve my business.',
      de: 'Wir haben 8 Jahre auf Empfehlungen gesetzt. BonusClub hat uns eine Pipeline gebaut, die nicht mehr vom Zufall abhängt.',
    },
  },
  {
    image: './img/wavy_1.jpg',
    name: 'Orient Analytics',
    role: { en: 'Software Company', de: 'Software Company' },
    company: 'Orient Analytics',
    website: 'https://orientanalytics.com/',
    industry: { en: 'Software Company', de: 'Software Company' },
    before: 4,
    after: 12,
    timeframe: { en: '90 days', de: '90 Tage' },
    quote: {
      en: 'BonusClub gave us the structure we were missing. Predictable pipeline, qualified leads, zero guesswork.',
      de: 'BonusClub hat uns die Struktur gegeben, die uns gefehlt hat. Planbare Pipeline, qualifizierte Leads, kein Raten mehr.',
    },
  },
  {
    image: './img/sarah.webp',
    name: 'Sarah Rickmers',
    role: { en: 'Web & Design', de: 'Web & Design' },
    company: '',
    website: 'https://visuellemagie-design.de',
    industry: { en: 'Web & Design', de: 'Web & Design' },
    before: 5,
    after: 19,
    timeframe: { en: '90 days', de: '90 Tage' },
    quote: {
      en: 'I was putting out a lot of content, sometimes 3 to 4 posts a week. But revenue was still unpredictable. BonusClub showed me how to reach decision-makers directly, instead of waiting to be discovered.',
      de: 'Ich habe viel Content gemacht. Teilweise 3-4 Posts pro Woche. Aber Umsatz war trotzdem nicht planbar. BonusClub hat mir gezeigt, wie ich gezielt auf Entscheider zugehe - statt darauf zu warten, entdeckt zu werden.',
    },
  },
];

const UseCaseCard = ({ uc, language, t }) => {
  const [ref, visible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`usecase-card scroll-fade-in ${visible ? 'is-visible' : ''}`}
    >
      {/* LinkedIn-style profile header */}
      <div className="usecase-card-header">
        <img src={uc.image} alt={uc.name} className="usecase-card-avatar" />
        <div className="usecase-card-header-body">
          <div className="usecase-card-header-top">
            <div className="usecase-card-identity">
              <h3 className="usecase-card-name">{uc.name}</h3>
              {(uc.role[language] || uc.company) && (
                <p className="usecase-card-headline">
                  {[uc.role[language], uc.company].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <div className="usecase-card-badges">
              {uc.website && (
                <span className="usecase-card-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                  {uc.website}
                </span>
              )}
              {uc.industry[language] && (
                <span className="usecase-card-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
                  {uc.industry[language]}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="usecase-card-body">
        <p className="usecase-card-quote">"{uc.quote[language]}"</p>

        <div className="usecase-card-metrics">
          <div className="usecase-card-metric">
            <span className="usecase-card-metric-label">{t.before}</span>
            <span className="usecase-card-metric-value usecase-card-metric-value--before">{uc.before}</span>
            <span className="usecase-card-metric-unit">{uc.unit ? uc.unit[language] : t.meetingsUnit}</span>
          </div>
          <div className="usecase-card-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </div>
          <div className="usecase-card-metric">
            <span className="usecase-card-metric-label">{t.after}</span>
            <span className="usecase-card-metric-value usecase-card-metric-value--after">{uc.after}</span>
            <span className="usecase-card-metric-unit">{uc.unit ? uc.unit[language] : t.meetingsUnit}</span>
          </div>
          <div className="usecase-card-timeframe">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            {t.within} {uc.timeframe[language]}
          </div>
        </div>
      </div>
    </div>
  );
};

const UseCaseSection = () => {
  const { language } = useLanguage();
  const t = translations[language].useCaseSection;
  const [labelRef, labelVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="usecase-section">
      <div className="usecase-section-inner">
        <div
          ref={labelRef}
          className={`usecase-section-header scroll-fade-in ${labelVisible ? 'is-visible' : ''}`}
        >
          <span className="section-label">{t.label}</span>
          <h2 className="usecase-section-title">{t.title}<br /><span className="chnodes-title-highlight">{t.titleHighlight}</span></h2>
          <p className="usecase-section-subtitle">{t.subtitle}</p>
        </div>

        <div className="usecase-list">
          {useCases.map((uc, i) => (
            <UseCaseCard key={i} uc={uc} language={language} t={t} />
          ))}
        </div>

        <div className="usecase-section-more">
          <div className="usecase-section-more-line" />
          <span className="usecase-section-more-text">{t.moreLabel}</span>
          <div className="usecase-section-more-line" />
        </div>
      </div>
    </section>
  );
};

export default UseCaseSection;
